'use client'

import Image from 'next/image'
import Link from 'next/link'
// import { Button } from '@/components/ui/button'

import { TalentPoolForm, type TalentPoolFormStep } from './talent-pool-form'
import { TalentPoolLandingContent } from './talent-pool-landing-content'
import { TalentPoolLandingStickyCta } from './talent-pool-landing-sticky-cta'
import { useTalentPoolPageState } from './talent-pool-page-state'
import { TalentPoolStepIndicator } from './talent-pool-step-indicator'
import { TalentPoolThankYouView } from './talent-pool-thank-you-view'

export function TalentPoolPageClient() {
  const state = useTalentPoolPageState()
  const { copy } = state

  // No hydration gate here. Returning null until the zustand store rehydrated
  // meant the server sent an empty page: crawlers saw 21 characters and no
  // heading, which is what Site Audit reported as "missing h1" on /talent-pool.
  // The landing step is the default state on both server and first client
  // render (`step` is useState('landing'), and the persisted keys are draft,
  // storedFileName, submittedAt and submittedCvId, none of which change which
  // view renders), so rendering it straight away is hydration-safe. Returning
  // visitors with a persisted submission still get the thank-you view, one
  // frame later, once `shouldShowThankYou` flips after rehydration.
  if (state.shouldShowThankYou) {
    return <TalentPoolThankYouView userName={state.draft.fullName || null} />
  }

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-border-primary bg-white dark:bg-background">
        <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-12">
          <Link href="/" aria-label="Hyred home">
            <Image
              src={state.logoSrc}
              alt="Hyred logo"
              width={136}
              height={42}
              className="h-[42px] w-auto"
              priority
            />
          </Link>
          <div className="flex items-center gap-3">
            {state.isLanding && (
              <div className="hidden items-center gap-1 md:flex">
                {copy.landing.navLinks.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="flex h-11 items-center rounded-lg px-5 text-base font-semibold text-[#222222] transition-colors hover:bg-[#f4f4f4]"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
            {/*
            <Button
              asChild
              className="h-11 rounded-lg bg-brand-primary px-[18px] text-base font-semibold text-white shadow-button hover:bg-brand-primary-hover"
            >
              <Link href="/">Review CV Gratis</Link>
            </Button>
            */}
          </div>
        </div>
      </nav>

      <main
        className={`mx-auto w-full ${state.mainMaxWidth} px-4 pb-16 pt-8 sm:px-6 lg:px-8`}
      >
        {state.isLanding && (
          <TalentPoolLandingContent
            file={state.file}
            storedFileName={state.storedFileName}
            isDragging={state.isDragging}
            submitError={state.submitError}
            canGoToPersonal={state.canGoToPersonal}
            fileInputRef={state.fileInputRef}
            onDragOver={state.handleUploadAreaDragOver}
            onDragLeave={state.handleUploadAreaDragLeave}
            onDrop={state.handleUploadAreaDrop}
            onOpenPicker={state.openFilePicker}
            onUploadAreaKeyDown={state.handleUploadAreaKeyDown}
            onFileInputChange={state.handleFileInputChange}
            onRemoveFile={state.removeFile}
            onLandingNext={state.handleLandingNext}
            onScrollToUpload={state.scrollToUpload}
          />
        )}

        {state.stepIndex >= 0 && (
          <TalentPoolStepIndicator
            stepLabels={state.stepLabels}
            stepIndex={state.stepIndex}
          />
        )}

        {state.isFormStep && (
          <TalentPoolForm
            step={state.step as TalentPoolFormStep}
            copy={copy}
            draft={state.draft}
            fileName={state.fileName}
            fieldErrors={state.fieldErrors}
            submitError={state.submitError}
            isSubmitting={state.isSubmitting}
            isAuthenticating={state.isAuthenticating}
            onBack={state.goBack}
            onNext={state.goNext}
            onSubmit={state.handleSubmit}
            setDraft={state.setDraft}
            setFieldErrors={state.setFieldErrors}
          />
        )}
      </main>

      {state.isLanding && (
        <TalentPoolLandingStickyCta onScrollToUpload={state.scrollToUpload} />
      )}
    </>
  )
}
